(() => {
  const ROOT_SELECTOR = '[data-bs-root]';
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

  class BauhausStudio {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId || '';
      this.overlay = root.querySelector('[data-bs-overlay]');
      this.mobileNav = root.querySelector('[data-bs-mobile-nav]');
      this.mobileNavToggle = root.querySelector('[data-bs-mobile-nav-toggle]');
      this.roomDrawer = root.querySelector('[data-bs-room-drawer]');
      this.productDrawer = root.querySelector('[data-bs-product-drawer]');
      this.productContent = root.querySelector('[data-bs-product-content]');
      this.residenceIndex = root.querySelector('[data-bs-residence-index]');
      this.collection = root.querySelector('.bs-collection');
      this.toast = root.querySelector('[data-bs-toast]');
      this.scenes = [...root.querySelectorAll('[data-bs-scene]')];
      this.roomScenes = this.scenes.filter((scene) => scene.dataset.bsScene !== 'hero');
      this.raf = 0;
      this.toastTimer = 0;
      this.lastFocused = null;
      this.activeSceneKey = '';
      this.residenceLabel = root.dataset.residenceLabel || 'Residence';
      this.objectsLabel = root.dataset.objectsLabel || 'Objects';
      this.boundOnScroll = this.onScroll.bind(this);
      this.boundOnKeydown = this.onKeydown.bind(this);
      this.init();
    }

    init() {
      this.applyThemeVisibility();
      this.bindClicks();
      this.bindVariantControls();
      this.restoreSaveState();
      this.updateSceneStepStates();
      this.onScroll();
      window.addEventListener('scroll', this.boundOnScroll, { passive: true });
      window.addEventListener('resize', this.boundOnScroll, { passive: true });
      document.addEventListener('keydown', this.boundOnKeydown);
    }

    destroy() {
      window.removeEventListener('scroll', this.boundOnScroll);
      window.removeEventListener('resize', this.boundOnScroll);
      document.removeEventListener('keydown', this.boundOnKeydown);
      cancelAnimationFrame(this.raf);
    }

    applyThemeVisibility() {
      document.body.classList.toggle('bs-hide-theme-header', this.root.dataset.hideHeader === 'true');
      document.body.classList.toggle('bs-hide-theme-footer', this.root.dataset.hideFooter === 'true');
    }

    bindClicks() {
      this.root.addEventListener('click', (event) => {
        const scrollButton = event.target.closest('[data-bs-scroll]');
        if (scrollButton) {
          this.scrollToSelector(scrollButton.dataset.bsScroll);
          this.closeMobileNav();
          return;
        }

        const mobileToggle = event.target.closest('[data-bs-mobile-nav-toggle]');
        if (mobileToggle) {
          this.mobileNav?.classList.contains('is-open') ? this.closeMobileNav() : this.openMobileNav(mobileToggle);
          return;
        }

        if (event.target.closest('[data-bs-mobile-nav-close]')) {
          this.closeMobileNav();
          return;
        }

        const prevButton = event.target.closest('[data-bs-prev-scene]');
        if (prevButton) {
          this.stepScene(prevButton.closest('[data-bs-scene]'), -1);
          return;
        }

        const nextButton = event.target.closest('[data-bs-next-scene]');
        if (nextButton) {
          this.stepScene(nextButton.closest('[data-bs-scene]'), 1);
          return;
        }

        const lightButton = event.target.closest('[data-bs-light]');
        if (lightButton) {
          const scene = lightButton.closest('[data-bs-scene]');
          if (scene) this.toggleLight(scene, lightButton);
          return;
        }

        const roomButton = event.target.closest('[data-bs-open-room]');
        if (roomButton) {
          this.openRoomDrawer(roomButton.dataset.bsOpenRoom, roomButton);
          return;
        }

        const productButton = event.target.closest('[data-bs-open-product]');
        if (productButton) {
          this.openProduct(productButton.dataset.bsOpenProduct, productButton);
          return;
        }

        if (event.target.closest('[data-bs-close]')) {
          this.closePanels();
          return;
        }

        const saveButton = event.target.closest('[data-bs-save]');
        if (saveButton) {
          this.toggleSave();
          return;
        }

        if (event.target === this.overlay) this.closePanels();
      });
    }

    bindVariantControls() {
      this.root.addEventListener('change', (event) => {
        const select = event.target.closest('[data-bs-variant-select]');
        if (!select) return;

        const panel = select.closest('[data-bs-product-panel]');
        if (!panel) return;

        const selected = select.options[select.selectedIndex];
        const hiddenInput = panel.querySelector('[data-bs-variant-input]');
        const price = panel.querySelector('[data-bs-product-price]');
        const image = panel.querySelector('.bs-product__image');
        const submit = panel.querySelector('.bs-product__add');

        if (hiddenInput) hiddenInput.value = selected.value;
        if (price && selected.dataset.price) price.textContent = selected.dataset.price;
        if (image && selected.dataset.image) image.src = selected.dataset.image;
        if (submit) {
          const disabled = selected.disabled;
          submit.disabled = disabled;
          submit.setAttribute('aria-disabled', String(disabled));
          const label = submit.querySelector('span:not(.sold-out-message):not(.loading__spinner)');
          if (label) label.textContent = disabled ? 'Sold out' : 'Add to cart';
        }
      });
    }

    scrollToSelector(selector) {
      if (!selector) return;
      let target = null;
      try {
        target = document.querySelector(selector);
      } catch (error) {
        return;
      }
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    onScroll() {
      cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(() => {
        const scrollTop = window.scrollY || document.documentElement.scrollTop;
        const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
        const progress = clamp(scrollTop / max);
        const bar = this.root.querySelector('[data-bs-progress]');
        if (bar) bar.style.width = `${progress * 100}%`;

        let activeScene = null;
        let activeDistance = Infinity;

        this.scenes.forEach((scene) => {
          const rect = scene.getBoundingClientRect();
          const travel = Math.max(1, rect.height - window.innerHeight);
          const local = clamp((-rect.top) / travel);
          scene.style.setProperty('--bs-scroll-progress', local.toFixed(4));

          const distance = Math.abs(rect.top + rect.height * 0.5 - window.innerHeight * 0.5);
          if (distance < activeDistance) {
            activeDistance = distance;
            activeScene = scene;
          }
        });

        const residenceDistance = this.elementCenterDistance(this.residenceIndex);
        const collectionDistance = this.elementCenterDistance(this.collection);
        const sceneDistance = activeScene ? activeDistance : Infinity;

        if (this.residenceIndex && residenceDistance < sceneDistance && residenceDistance <= collectionDistance) {
          this.setActiveNavigation('', this.residenceLabel, `#${this.residenceIndex.id}`);
        } else if (this.collection && collectionDistance < sceneDistance && collectionDistance < residenceDistance) {
          this.setActiveNavigation('', this.objectsLabel, `#${this.collection.id}`);
        } else if (activeScene) {
          this.setActiveNavigation(
            activeScene.dataset.bsScene || '',
            activeScene.dataset.bsSceneLabel || this.residenceLabel,
            `#${activeScene.id}`
          );
        }
      });
    }

    elementCenterDistance(element) {
      if (!element) return Infinity;
      const rect = element.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) {
        return Math.abs(rect.top + rect.height * 0.5 - window.innerHeight * 0.5) + window.innerHeight;
      }
      return Math.abs(rect.top + rect.height * 0.5 - window.innerHeight * 0.5);
    }

    setActiveNavigation(sceneKey, label, selector) {
      this.activeSceneKey = sceneKey;
      this.root.querySelectorAll('[data-bs-scroll]').forEach((button) => {
        const isActive = button.dataset.bsScroll === selector || (sceneKey && button.dataset.bsNavScene === sceneKey);
        button.classList.toggle('is-active', Boolean(isActive));
        if (isActive) button.setAttribute('aria-current', 'location');
        else button.removeAttribute('aria-current');
      });
      this.root.querySelectorAll('[data-bs-current-scene]').forEach((node) => {
        node.textContent = label;
      });
    }

    updateSceneStepStates() {
      this.roomScenes.forEach((scene, index) => {
        const prev = scene.querySelector('[data-bs-prev-scene]');
        const next = scene.querySelector('[data-bs-next-scene]');
        if (prev) prev.disabled = false;
        if (next) next.disabled = false;
        const label = scene.querySelector('[data-bs-step-label]');
        if (label) label.textContent = scene.dataset.bsSceneLabel || `Room ${index + 1}`;
      });
    }

    stepScene(scene, direction) {
      if (!scene) return;
      const index = this.roomScenes.indexOf(scene);
      if (index < 0) return;
      const nextIndex = index + direction;
      if (nextIndex < 0) {
        const hero = this.scenes.find((item) => item.dataset.bsScene === 'hero');
        if (this.residenceIndex) this.residenceIndex.scrollIntoView({ behavior: 'smooth', block: 'start' });
        else (hero || this.scenes[0])?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (nextIndex >= this.roomScenes.length) {
        this.collection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      this.roomScenes[nextIndex]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    toggleLight(scene, button) {
      const isOn = scene.classList.toggle('is-light-on');
      button.setAttribute('aria-pressed', String(isOn));
      const label = button.querySelector('[data-bs-light-label]');
      if (label) label.textContent = isOn ? button.dataset.labelOff : button.dataset.labelOn;
    }

    openMobileNav(trigger) {
      if (!this.mobileNav) return;
      this.closePanels(false);
      this.lastFocused = trigger || document.activeElement;
      this.mobileNav.classList.add('is-open');
      this.mobileNav.setAttribute('aria-hidden', 'false');
      this.mobileNavToggle?.setAttribute('aria-expanded', 'true');
      this.overlay?.classList.add('is-open');
      this.overlay?.setAttribute('aria-hidden', 'false');
      document.body.classList.add('bs-panel-open');
      requestAnimationFrame(() => this.mobileNav?.querySelector('button')?.focus());
    }

    closeMobileNav(restoreFocus = false) {
      this.mobileNav?.classList.remove('is-open');
      this.mobileNav?.setAttribute('aria-hidden', 'true');
      this.mobileNavToggle?.setAttribute('aria-expanded', 'false');
      if (!this.roomDrawer?.classList.contains('is-open') && !this.productDrawer?.classList.contains('is-open')) {
        this.overlay?.classList.remove('is-open');
        this.overlay?.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('bs-panel-open');
      }
      if (restoreFocus && this.lastFocused instanceof HTMLElement) this.lastFocused.focus({ preventScroll: true });
    }

    openRoomDrawer(sceneKey, trigger) {
      this.lastFocused = trigger || document.activeElement;
      this.root.querySelectorAll('[data-bs-room-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.bsRoomPanel !== sceneKey;
      });
      this.openPanel(this.roomDrawer);
    }

    openProduct(templateId, trigger) {
      const template = this.root.querySelector(`#${CSS.escape(templateId)}`);
      if (!template || !this.productContent) return;
      this.lastFocused = trigger || document.activeElement;
      this.productContent.replaceChildren(template.content.cloneNode(true));
      this.openPanel(this.productDrawer);
    }

    openPanel(panel) {
      if (!panel || !this.overlay) return;
      this.closeMobileNav(false);
      this.roomDrawer?.classList.remove('is-open');
      this.productDrawer?.classList.remove('is-open');
      this.roomDrawer?.setAttribute('aria-hidden', 'true');
      this.productDrawer?.setAttribute('aria-hidden', 'true');
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      this.overlay.classList.add('is-open');
      this.overlay.setAttribute('aria-hidden', 'false');
      document.body.classList.add('bs-panel-open');
      requestAnimationFrame(() => panel.querySelector('[data-bs-close]')?.focus());
    }

    closePanels(restoreFocus = true) {
      [this.roomDrawer, this.productDrawer].forEach((panel) => {
        panel?.classList.remove('is-open');
        panel?.setAttribute('aria-hidden', 'true');
      });
      this.mobileNav?.classList.remove('is-open');
      this.mobileNav?.setAttribute('aria-hidden', 'true');
      this.mobileNavToggle?.setAttribute('aria-expanded', 'false');
      this.overlay?.classList.remove('is-open');
      this.overlay?.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('bs-panel-open');
      if (restoreFocus && this.lastFocused instanceof HTMLElement) this.lastFocused.focus({ preventScroll: true });
    }

    onKeydown(event) {
      if (event.key === 'Escape') this.closePanels();
    }

    toggleSave() {
      const key = this.root.dataset.saveKey;
      if (!key) return;
      const saved = localStorage.getItem(key) === '1';
      if (saved) localStorage.removeItem(key);
      else localStorage.setItem(key, '1');
      this.applySaveState(!saved);
      this.showToast(!saved ? 'Layout saved in this browser' : 'Layout removed');
    }

    restoreSaveState() {
      const key = this.root.dataset.saveKey;
      if (!key) return;
      this.applySaveState(localStorage.getItem(key) === '1');
    }

    applySaveState(saved) {
      this.root.querySelectorAll('[data-bs-save]').forEach((button) => {
        button.classList.toggle('is-saved', saved);
        button.setAttribute('aria-pressed', String(saved));
        const label = button.querySelector('span');
        if (label) label.textContent = saved ? button.dataset.labelSaved : button.dataset.labelDefault;
      });
    }

    showToast(message) {
      if (!this.toast) return;
      clearTimeout(this.toastTimer);
      this.toast.textContent = message;
      this.toast.classList.add('is-visible');
      this.toastTimer = window.setTimeout(() => this.toast?.classList.remove('is-visible'), 2200);
    }
  }

  const instances = new WeakMap();
  const initRoot = (root) => {
    if (!root || instances.has(root)) return;
    instances.set(root, new BauhausStudio(root));
  };
  const initAll = (scope = document) => scope.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector?.(ROOT_SELECTOR);
    const instance = root ? instances.get(root) : null;
    instance?.destroy();
  });
  document.addEventListener('shopify:block:select', (event) => {
    const block = event.target;
    const scene = block.closest?.('[data-bs-scene]');
    if (scene) scene.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
})();
